
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserUserEntity from 'Models/Security/Acl/UserUserEntity';
import VisitorsUserEntity from 'Models/Security/Acl/VisitorsUserEntity';
import { IAcl } from 'Models/Security/IAcl';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeEnumFetchFunction,
	makeFetchManyToManyFunc,
	makeFetchOneToManyFunc,
	makeJoinEqualsFunc,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import * as Enums from '../Enums';
import { userTypeOptions } from '../Enums/MappedEnumOptions';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
import { computed } from 'mobx';
import { UserPermissions } from 'Models/UserPermissions';

export type UserEntityErrors = {
	[K in keyof UserEntity]?: string;
};

export interface IUserEntityAttributes extends IModelAttributes {
	email: string;
	userName: string;
	twoFactorEnabled: boolean;
	phone: string;
	creditApprovalLimit: number | null;
	isActive: boolean;
	firstName: string;
	canAccessPPSR: boolean;
	enableEmailNotifications: boolean;
	legacyID: number;
	canAccessApprove: boolean;
	canAccessIntel: boolean;
	lastName: string;
	userType: Enums.userType;
	permissionOverrides: string;

	atbFiles: Array<
		| Models.AtbFileEntity
		| Models.IAtbFileEntityAttributes
	>;
	customerAuditss: Array<
		| Models.CustomerAuditEntity
		| Models.ICustomerAuditEntityAttributes
	>;
	paymentmethods: Array<
		| Models.PaymentMethodEntity
		| Models.IPaymentMethodEntityAttributes
	>;
	referredRegistrationss: Array<
		| Models.RegistrationEntity
		| Models.IRegistrationEntityAttributes
	>;
	registration?: Models.RegistrationEntity |
	Models.IRegistrationEntityAttributes;
	reportRequests: Array<
		| Models.ReportRequestEntity
		| Models.IReportRequestEntityAttributes
	>;
	businessEntitys: Array<
		| Models.BusinessEntityUser
		| Models.IBusinessEntityUserAttributes
	>;
	organisationId?: string;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	password?: string; // Used when creating a new user through GraphQL.

	canAccessApproveAggregate: boolean;
	canAccessPPSRAggregate: boolean;
	canAccessIntelAggregate: boolean;
}

@entity('UserEntity', 'User')
export default class UserEntity extends Model implements IUserEntityAttributes {

	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsUserEntity(),
		new UserUserEntity(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		'email',
		'userName',
		'twoFactorEnabled',
	];

	@Validators.Email()
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Email',
		displayType: 'displayfield',
		order: 10,
		createFieldType: 'textfield',
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public email: string;

	@observable
	@attribute()
	@CRUD({
		name: 'TwoFactorEnabled',
		displayType: 'displayfield',
		order: 20,
		createFieldType: 'textfield',
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public twoFactorEnabled: boolean;

	@observable
	@attribute()
	@CRUD({
		name: 'UserName',
		displayType: 'hidden',
	})
	public userName: string;

	@Validators.Length(12, 128)
	@observable
	@CRUD({
		name: 'Password',
		displayType: 'hidden',
		order: 30,
		createFieldType: 'password',
	})
	public password: string;

	@Validators.Custom('Password Match', (e: string, target: UserEntity) => {
		return new Promise(resolve => resolve(target.password !== e ? 'Password fields do not match' : null));
	})
	@observable
	@CRUD({
		name: 'Confirm Password',
		displayType: 'hidden',
		order: 40,
		createFieldType: 'password',
	})
	public _confirmPassword: string;

	/**
	 * Phone
	 */
	@observable
	@attribute<UserEntity, string>()
	@CRUD({
		name: 'Phone',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public phone: string;

	@Validators.Numeric()
	@observable
	@attribute<UserEntity, number>()
	@CRUD({
		name: 'Credit Approval Limit',
		displayType: 'textfield',
		order: 60,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public creditApprovalLimit: number | null;

	/**
	 * Is Active
	 */
	@observable
	@attribute<UserEntity, boolean>()
	@CRUD({
		name: 'Is Active',
		displayType: 'checkbox',
		order: 70,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public isActive: boolean;

	/**
	 * First Name
	 */
	@observable
	@attribute<UserEntity, string>()
	@CRUD({
		name: 'First Name',
		displayType: 'textfield',
		order: 80,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public firstName: string;

	/**
	 * Can Access PPSR
	 */
	@observable
	@attribute<UserEntity, boolean>()
	@CRUD({
		name: 'Can Access PPSR',
		displayType: 'checkbox',
		order: 90,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public canAccessPPSR: boolean;

	@observable
	@attribute<UserEntity, boolean>()
	@CRUD({
		name: 'Enable Email Notifications',
		displayType: 'checkbox',
		order: 100,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public enableEmailNotifications: boolean;

	@Validators.Integer()
	@observable
	@attribute<UserEntity, number>()
	@CRUD({
		name: 'Legacy ID',
		displayType: 'textfield',
		order: 110,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public legacyID: number;

	/**
	 * Can Access Approve
	 */
	@observable
	@attribute<UserEntity, boolean>()
	@CRUD({
		name: 'Can Access Approve',
		displayType: 'checkbox',
		order: 120,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public canAccessApprove: boolean;

	/**
	 * Can Access Intel
	 */
	@observable
	@attribute<UserEntity, boolean>()
	@CRUD({
		name: 'Can Access Intel',
		displayType: 'checkbox',
		order: 130,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public canAccessIntel: boolean;

	/**
	 * Last Name
	 */
	@observable
	@attribute<UserEntity, string>()
	@CRUD({
		name: 'Last Name',
		displayType: 'textfield',
		order: 140,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public lastName: string;

	/**
	 * User Type
	 */
	@observable
	@attribute<UserEntity, Enums.userType>()
	@CRUD({
		name: 'User Type',
		displayType: 'enum-combobox',
		order: 150,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, userTypeOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(userTypeOptions),
		displayFunction: (attr: Enums.userType) => userTypeOptions[attr],
	})
	public userType: Enums.userType;

	/**
	 * Permission Overrides
	 */
	@observable
	@attribute<UserEntity, string>()
	@CRUD({
		name: 'Permission Overrides',
		displayType: 'textfield',
		order: 160,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public permissionOverrides: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'ATB Files',
		displayType: 'reference-multicombobox',
		order: 170,
		referenceTypeFunc: () => Models.AtbFileEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'atbFiles',
			oppositeEntity: () => Models.AtbFileEntity,
		}),
	})
	public atbFiles: Models.AtbFileEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'Customer Auditss',
		displayType: 'reference-multicombobox',
		order: 180,
		referenceTypeFunc: () => Models.CustomerAuditEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'customerAuditss',
			oppositeEntity: () => Models.CustomerAuditEntity,
		}),
	})
	public customerAuditss: Models.CustomerAuditEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'PaymentMethods',
		displayType: 'reference-multicombobox',
		order: 190,
		referenceTypeFunc: () => Models.PaymentMethodEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'paymentmethods',
			oppositeEntity: () => Models.PaymentMethodEntity,
		}),
	})
	public paymentmethods: Models.PaymentMethodEntity[] = [];

	/**
	 * Keep track of users who create referrals
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'Referred Registrationss',
		displayType: 'reference-multicombobox',
		order: 200,
		referenceTypeFunc: () => Models.RegistrationEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'referredRegistrationss',
			oppositeEntity: () => Models.RegistrationEntity,
		}),
	})
	public referredRegistrationss: Models.RegistrationEntity[] = [];

	/**
	 * Registration which created this user
	 */
	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		name: 'Registration',
		displayType: 'reference-combobox',
		order: 210,
		referenceTypeFunc: () => Models.RegistrationEntity,
		optionEqualFunc: (model, option) => model.id === option,
		inputProps: {
			fetchReferenceEntity: true,
		},
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'registrations',
			oppositeEntity: () => Models.RegistrationEntity,
		}),
	})
	public registration?: Models.RegistrationEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'Report Requests',
		displayType: 'reference-multicombobox',
		order: 220,
		referenceTypeFunc: () => Models.ReportRequestEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'reportRequests',
			oppositeEntity: () => Models.ReportRequestEntity,
		}),
	})
	public reportRequests: Models.ReportRequestEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'Business Entity',
		displayType: 'reference-multicombobox',
		order: 230,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.BusinessEntityUser,
		optionEqualFunc: makeJoinEqualsFunc('businessEntityId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'userEntity',
			oppositeEntityName: 'businessEntity',
			relationName: 'user',
			relationOppositeName: 'businessEntity',
			entity: () => Models.UserEntity,
			joinEntity: () => Models.BusinessEntityUser,
			oppositeEntity: () => Models.BusinessEntity,
		}),
	})
	public businessEntitys: Models.BusinessEntityUser[] = [];

	@observable
	@attribute()
	@CRUD({
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 240,
		referenceTypeFunc: () => Models.OrganisationEntity,
	})
	public organisationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;

	/**
	 * Can Access PPSR Aggregate
	 */
	@observable
	public canAccessPPSRAggregate: boolean;

	/**
	 * Can Access Approve Aggregate
	 */
	@observable
	public canAccessApproveAggregate: boolean;

	/**
	 * Can Access Intel Aggregate
	 */
	@observable
	public canAccessIntelAggregate: boolean;


	// Add custom getters and setters to convert string to JSON
	@computed get permissionOverridesObject(): UserPermissions | undefined {
		if (this.permissionOverrides === '' || this.permissionOverrides == null) {
			return undefined;
		}
		return new UserPermissions(JSON.parse(this.permissionOverrides));
	}

	set permissionOverridesObject(value: UserPermissions | undefined) {
		this.permissionOverrides = !value ? '' : JSON.stringify(value);
	}

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IUserEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IUserEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.userName !== undefined) {
				this.userName = attributes.userName;
			}
			if (attributes.twoFactorEnabled !== undefined) {
				this.twoFactorEnabled = attributes.twoFactorEnabled;
			}
			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.creditApprovalLimit !== undefined) {
				this.creditApprovalLimit = attributes.creditApprovalLimit;
			}
			if (attributes.isActive !== undefined) {
				this.isActive = attributes.isActive;
			}
			if (attributes.firstName !== undefined) {
				this.firstName = attributes.firstName;
			}
			if (attributes.canAccessPPSR !== undefined) {
				this.canAccessPPSR = attributes.canAccessPPSR;
			}
			if (attributes.enableEmailNotifications !== undefined) {
				this.enableEmailNotifications = attributes.enableEmailNotifications;
			}
			if (attributes.legacyID !== undefined) {
				this.legacyID = attributes.legacyID;
			}
			if (attributes.canAccessApprove !== undefined) {
				this.canAccessApprove = attributes.canAccessApprove;
			}
			if (attributes.canAccessIntel !== undefined) {
				this.canAccessIntel = attributes.canAccessIntel;
			}

			if (attributes.canAccessPPSRAggregate !== undefined) {
				this.canAccessPPSRAggregate = attributes.canAccessPPSRAggregate;
			}
			if (attributes.canAccessApproveAggregate !== undefined) {
				this.canAccessApproveAggregate = attributes.canAccessApproveAggregate;
			}
			if (attributes.canAccessIntelAggregate !== undefined) {
				this.canAccessIntelAggregate = attributes.canAccessIntelAggregate;
			}

			if (attributes.lastName !== undefined) {
				this.lastName = attributes.lastName;
			}
			if (attributes.userType !== undefined) {
				this.userType = attributes.userType;
			}
			if (attributes.permissionOverrides !== undefined) {
				this.permissionOverrides = attributes.permissionOverrides;
			}
			if (attributes.atbFiles !== undefined && Array.isArray(attributes.atbFiles)) {
				for (const model of attributes.atbFiles) {
					if (model instanceof Models.AtbFileEntity) {
						this.atbFiles.push(model);
					} else {
						this.atbFiles.push(new Models.AtbFileEntity(model));
					}
				}
			}
			if (attributes.customerAuditss !== undefined && Array.isArray(attributes.customerAuditss)) {
				for (const model of attributes.customerAuditss) {
					if (model instanceof Models.CustomerAuditEntity) {
						this.customerAuditss.push(model);
					} else {
						this.customerAuditss.push(new Models.CustomerAuditEntity(model));
					}
				}
			}
			if (attributes.paymentmethods !== undefined && Array.isArray(attributes.paymentmethods)) {
				for (const model of attributes.paymentmethods) {
					if (model instanceof Models.PaymentMethodEntity) {
						this.paymentmethods.push(model);
					} else {
						this.paymentmethods.push(new Models.PaymentMethodEntity(model));
					}
				}
			}
			if (attributes.referredRegistrationss !== undefined && Array.isArray(attributes.referredRegistrationss)) {
				for (const model of attributes.referredRegistrationss) {
					if (model instanceof Models.RegistrationEntity) {
						this.referredRegistrationss.push(model);
					} else {
						this.referredRegistrationss.push(new Models.RegistrationEntity(model));
					}
				}
			}
			if (attributes.registration !== undefined) {
				if (attributes.registration === null) {
					this.registration = attributes.registration;
				} else if (attributes.registration instanceof Models.RegistrationEntity) {
					this.registration = attributes.registration;
				} else {
					this.registration = new Models.RegistrationEntity(attributes.registration);
				}
			}
			if (attributes.reportRequests !== undefined && Array.isArray(attributes.reportRequests)) {
				for (const model of attributes.reportRequests) {
					if (model instanceof Models.ReportRequestEntity) {
						this.reportRequests.push(model);
					} else {
						this.reportRequests.push(new Models.ReportRequestEntity(model));
					}
				}
			}
			if (attributes.businessEntitys !== undefined && Array.isArray(attributes.businessEntitys)) {
				for (const model of attributes.businessEntitys) {
					if (model instanceof Models.BusinessEntityUser) {
						this.businessEntitys.push(model);
					} else {
						this.businessEntitys.push(new Models.BusinessEntityUser(model));
					}
				}
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		businessEntitys {
			${Models.BusinessEntityUser.getAttributes().join('\n')}
			businessEntity {
				${Models.BusinessEntity.getAttributes().join('\n')}
			}
		}
		atbFiles {
			${Models.AtbFileEntity.getAttributes().join('\n')}
		}
		customerAuditss {
			${Models.CustomerAuditEntity.getAttributes().join('\n')}
		}
		paymentmethods {
			${Models.PaymentMethodEntity.getAttributes().join('\n')}
		}
		referredRegistrationss {
			${Models.RegistrationEntity.getAttributes().join('\n')}
		}
		registration {
			${Models.RegistrationEntity.getAttributes().join('\n')}
		}
		reportRequests {
			${Models.ReportRequestEntity.getAttributes().join('\n')}
		}
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
			${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			businessEntitys: {},
			atbFiles: {},
			customerAuditss: {},
			paymentmethods: {},
			referredRegistrationss: {},
			registration: {},
			reportRequests: {},
		};

		if (formMode === 'create') {
			relationPath.password = {};

			if (this.password !== this._confirmPassword) {
				throw Error('Password fields do not match');
			}
		}
		return this.save(
			relationPath,
			{
				graphQlInputType: formMode === 'create'
					? `[${this.getModelName()}CreateInput]`
					: `[${this.getModelName()}Input]`,
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'customerAuditss',
							'referredRegistrationss',
							'registration',
							'businessEntitys',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.email;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(UserEntity.prototype, 'created');
CRUD(modifiedAttr)(UserEntity.prototype, 'modified');
